import { useState } from "react";
import { Box, Button, useMediaQuery, Typography, Alert } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { baseURL } from "services/API";
import LinearProgress from "@mui/material/LinearProgress";
import TopSnack from "components/TopSnack";
import useLandlordOtpLogin from "services/hooks/useLandlordOtpLogin";
import OtpLoginFields from "./components/OtpLoginFields";
import RegisterLandlordForm from "./components/RegisterLandlordForm";
import {
  buildRegisterPayload,
  initialValuesRegister,
  registerSchema,
} from "./registerSchema";

const requestOtpSchema = yup.object().shape({
  phone_number: yup.string().required("required"),
});

const verifyOtpSchema = yup.object().shape({
  otp_code: yup.string().required("required"),
});

const Form = () => {
  const [pageType, setPageType] = useState("login");
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const isLogin = pageType === "login";
  const isRegister = pageType === "register";
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [openSnack, setOpenSnack] = useState({ open: false, message: "", severity: "" });
  const [loginStep, setLoginStep] = useState("requestOtp");
  const [otpSession, setOtpSession] = useState({ relatedUser: null, phoneNumber: "" });
  const [loginPhoneNumber, setLoginPhoneNumber] = useState("");
  const { requestOtp, verifyOtp, completeLogin } = useLandlordOtpLogin();

  const initialValuesLogin = {
    phone_number: loginPhoneNumber,
    otp_code: "",
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack((prev) => ({ ...prev, open: false }));
  };

  const register = async (values, onSubmitProps) => {
    setIsLoading(true);
    setError("");

    try {
      const savedUserResponse = await fetch(`${baseURL}/accounts/users/landlords`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildRegisterPayload(values)),
      });

      const savedUser = await savedUserResponse.json();
      if (savedUser.status === 201) {
        onSubmitProps.resetForm();
        setLoginPhoneNumber(values.contact);
        setOpenSnack({
          open: true,
          message: savedUser.data.message,
          severity: "success",
        });
        setPageType("login");
        setLoginStep("requestOtp");
        setOtpSession({ relatedUser: null, phoneNumber: "" });
      } else {
        setError(savedUser.error || "Registration Failed, Please try again");
      }
    } catch (err) {
      setError("Registration failed, please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async (setFieldValue) => {
    setIsLoading(true);
    setError("");

    try {
      const otpRequestResponse = await requestOtp(otpSession.phoneNumber);

      if (otpRequestResponse.status === 200) {
        setOtpSession({
          relatedUser: otpRequestResponse?.data?.user_details?.id,
          phoneNumber: otpSession.phoneNumber,
        });
        setFieldValue("otp_code", "");
        setOpenSnack({
          open: true,
          message: "A new OTP has been sent. Enter the code to continue.",
          severity: "success",
        });
      } else {
        setError(otpRequestResponse.error || "Failed to send OTP, please try again");
      }
    } catch (err) {
      setError("Failed to send OTP, please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (values, onSubmitProps) => {
    setIsLoading(true);
    setError("");

    try {
      if (loginStep === "requestOtp") {
        const otpRequestResponse = await requestOtp(values.phone_number);

        if (otpRequestResponse.status === 200) {
          setOtpSession({
            relatedUser: otpRequestResponse?.data?.user_details?.id,
            phoneNumber: values.phone_number,
          });
          setLoginStep("verifyOtp");
          onSubmitProps.setFieldValue("otp_code", "");
          setOpenSnack({
            open: true,
            message: "OTP sent successfully. Enter the code to continue.",
            severity: "success",
          });
        } else {
          setError(otpRequestResponse.error || "Landlord account doesn't exist");
        }
      } else {
        const otpVerificationResponse = await verifyOtp({
          relatedUser: otpSession.relatedUser,
          otpCode: values.otp_code,
        });

        if (otpVerificationResponse.status === 200) {
          onSubmitProps.resetForm();
          await completeLogin(otpVerificationResponse.data);
        } else {
          setError(
            otpVerificationResponse.error ||
              "Invalid OTP provided, Please provide the correct OTP."
          );
        }
      }
    } catch (err) {
      setError("Login failed, please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (values, onSubmitProps) => {
    if (isLogin) await login(values, onSubmitProps);
    if (isRegister) await register(values, onSubmitProps);
  };

  return (
    <>
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={isLogin ? initialValuesLogin : initialValuesRegister}
        validationSchema={
          isLogin ? (loginStep === "requestOtp" ? requestOtpSchema : verifyOtpSchema) : registerSchema
        }
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          resetForm,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box gap="30px" marginBottom={2}>
              {error && <Alert severity="error">{error}</Alert>}
            </Box>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {isRegister ? (
                <RegisterLandlordForm
                  values={values}
                  touched={touched}
                  errors={errors}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />
              ) : (
                <OtpLoginFields
                  values={values}
                  touched={touched}
                  errors={errors}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  loginStep={loginStep}
                  otpSession={otpSession}
                  isLoading={isLoading}
                  onResendOtp={() => handleResendOtp(setFieldValue)}
                  onEditPhoneNumber={() => {
                    setLoginPhoneNumber(otpSession.phoneNumber);
                    setLoginStep("requestOtp");
                    setOtpSession({ relatedUser: null, phoneNumber: "" });
                    setError("");
                    setFieldValue("otp_code", "");
                  }}
                />
              )}
            </Box>

            <Box>
              {isLoading ? (
                <LinearProgress
                  color="warning"
                  sx={{
                    m: "2rem 0",
                    p: "1rem",
                    backgroundColor: "#FCB200",
                    borderRadius: "2rem",
                  }}
                />
              ) : (
                <Button
                  fullWidth
                  type="submit"
                  disabled={
                    isRegister && (!values.terms_agreed || !values.agreement_template_id)
                  }
                  sx={{
                    m: "2rem 0",
                    p: "1rem",
                    borderRadius: "2rem",
                    fontSize: "16px",
                    fontWeight: 600,
                    backgroundColor: "#FCB200",
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: "#FCB200", color: "#89867f" },
                  }}
                >
                  {isLogin
                    ? loginStep === "requestOtp"
                      ? "REQUEST OTP"
                      : "VERIFY OTP"
                    : "REGISTER"}
                </Button>
              )}

              <Typography
                onClick={() => {
                  setPageType(isLogin ? "register" : "login");
                  setError("");
                  setLoginStep("requestOtp");
                  setOtpSession({ relatedUser: null, phoneNumber: "" });
                  resetForm();
                }}
                sx={{
                  textDecoration: "underline",
                  color: "#FCB200",
                  "&:hover": {
                    cursor: "pointer",
                    color: "#FCB200",
                  },
                }}
              >
                {isLogin
                  ? "Don't have an account? Sign Up here."
                  : "Already have an account? Login here."}
              </Typography>
            </Box>
          </form>
        )}
      </Formik>
      <TopSnack
        open={openSnack.open}
        handleCloseSnack={handleCloseSnack}
        message={openSnack.message}
        severity={openSnack.severity}
      />
    </>
  );
};

export default Form;
